import { Close, Notifications } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  Modal,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useNotification from "../../hooks/useNotification";
import { useSelector } from "react-redux";
import { BsBell } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { useReadNotificationMutation } from "../../redux/api/notificationApi";

export default function Notification({ children }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { agentId } = useSelector((state) => state.auth);
  const [notifications, setNotifications] = useState([]);
  // const [open, setOpen] = useState(false);
  const [readNotificationMutation] = useReadNotificationMutation();
  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const notification = useNotification(agentId);

  useEffect(() => {
    setNotifications(notification);
  }, [notification]);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleNavigateToNotification(notification) {
    setAnchorEl(null);
    switch (notification.tag) {
      case "prop":
        navigate(`/properties/?propertyId=${notification.id}`);
        break;
      case "task":
        navigate(`/tasks?taskId=${notification.id}`);
        break;
      case "ag-s":
        window.location.href = `/users/${notification.id}`;
        break;
      case "at-s":
        window.location.href = `/roles/${notification.id}`;
        break;
      case "agsu":
        navigate(`/users/${notification.id}`);
        break;
      case "agtp":
        navigate(`/roles/${notification.id}`);
        break;
      case "lead":
        navigate(`/leads?leadId=${notification.id}`, {
          replace: true,
        });
        break;
      default:
        break;
    }
  }

  const getNotificationIcon = (tag) => {
    const iconConfig = {
      prop: { icon: "🏠", color: "#3B82F6" },
      task: { icon: "✅", color: "#8B5CF6" },
      lead: { icon: "📈", color: "#F59E0B" },
      "ag-s": { icon: "👥", color: "#10B981" },
      "at-s": { icon: "🔑", color: "#EF4444" },
      agsu: { icon: "👤", color: "#8B5CF6" },
      agtp: { icon: "🎯", color: "#06B6D4" },
    };

    const config = iconConfig[tag] || { icon: "🔔", color: "#6B7280" };
    return config;
  };

  function handleClickNotification(notification) {
    const { notificationId: id, status } = notification;
    if (status === 0) {
      readNotificationMutation(id)
        .unwrap()
        .then((_) => {
          handleNavigateToNotification(notification);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      handleNavigateToNotification(notification);
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
    }
  };

  const unreadCount = notifications.filter((item) => item.status === 0).length;

  return (
    <>
      <IconButton
        color="primary"
        onClick={handleClick}
        sx={{
          position: "relative",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "scale(1.1)",
            backgroundColor: "rgba(86, 198, 98, 0.12)",
            "& .notification-bell": {
              animation: "ring 0.5s ease-in-out",
            },
          },
          "@keyframes ring": {
            "0%": { transform: "rotate(0deg)" },
            "25%": { transform: "rotate(15deg)" },
            "50%": { transform: "rotate(-15deg)" },
            "75%": { transform: "rotate(10deg)" },
            "100%": { transform: "rotate(0deg)" },
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              fontWeight: "bold",
              height: 18,
              minWidth: 18,
              boxShadow: (theme) =>
                `0 0 0 2px ${theme.palette.background.paper}`,
              background: "linear-gradient(135deg, #FF6B6B, #EE5A24)",
            },
          }}
        >
          <Notifications
            className="notification-bell"
            sx={{
              fontSize: 26,
              color: open ? "#56C662" : "text.secondary",
              transition: "color 0.3s ease",
            }}
          />
        </Badge>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 9999 }}
        transition
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 12] },
          },
          {
            name: "preventOverflow",
            options: {
              mainAxis: true,
              altAxis: true,
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <Paper
              elevation={16}
              sx={{
                width: { xs: "90vw", sm: 400 }, // 90% width on small screens
                maxHeight: { xs: "70vh", sm: 560 }, // smaller height on mobile
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.background.paper
                    : "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                backdropFilter: "blur(20px)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 20px 40px rgba(0,0,0,0.45)"
                    : "0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.8)",
                p: { xs: 1, sm: 0 }, // reduce padding on small screens
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* Header */}
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      background:
                        "linear-gradient(135deg, #56C662 0%, #4CAF50 100%)",
                      color: "white",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "1px",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        fontSize="1.1rem"
                      >
                        Notifications
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.9, fontSize: "0.75rem" }}
                      >
                        {unreadCount > 0
                          ? `${unreadCount} unread message${
                              unreadCount > 1 ? "s" : ""
                            }`
                          : "All caught up!"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {unreadCount > 0 && (
                        <Chip
                          label={`${unreadCount} New`}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            height: 24,
                            backdropFilter: "blur(10px)",
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Notifications List */}
                  <Box
                    sx={{
                      maxHeight: 480,
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#E2E8F0",
                        borderRadius: 3,
                        "&:hover": {
                          backgroundColor: "#CBD5E1",
                        },
                      },
                    }}
                  >
                    <Stack spacing={1} sx={{ p: { xs: 1, sm: 1 } }}>
                      {notifications.length === 0 ? (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 8,
                            color: "text.secondary",
                          }}
                        >
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              background: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)"
                                  : "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 2,
                            }}
                          >
                            <Notifications
                              sx={{
                                fontSize: 40,
                                opacity: 0.4,
                                color: "#64748B",
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body1"
                            fontWeight="500"
                            gutterBottom
                          >
                            No notifications
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            You're all caught up!
                          </Typography>
                        </Box>
                      ) : (
                        notifications.map((notif, index) => {
                          const isUnread = notif.status === 0;
                          const iconConfig = getNotificationIcon(notif.tag);

                          return (
                            <Paper
                              key={notif.notificationId}
                              onClick={() => handleClickNotification(notif)}
                              sx={{
                                p: 2.5,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                                cursor: "pointer",
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                borderRadius: 2.5,
                                background: isUnread
                                  ? "linear-gradient(135deg, rgba(86,198,98,0.08) 0%, rgba(86,198,98,0.02) 100%)"
                                  : (theme) => theme.palette.background.paper,
                                border: isUnread
                                  ? "1.5px solid rgba(86,198,98,0.2)"
                                  : (theme) => `1px solid ${theme.palette.divider}`,
                                position: "relative",
                                overflow: "hidden",
                                "&::before": isUnread
                                  ? {
                                      content: '""',
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 3,
                                      background:
                                        "linear-gradient(180deg, #56C662, #4CAF50)",
                                    }
                                  : {},
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                                  borderColor: "rgba(86,198,98,0.3)",
                                },
                              }}
                            >
                              {/* Content */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isUnread ? 600 : 500,
                                    color: isUnread
                                      ? "text.primary"
                                      : "text.secondary",
                                    lineHeight: 1.4,
                                    mb: 1,
                                  }}
                                >
                                  {notif.SenderName && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#56C662",
                                        mr: 0.5,
                                      }}
                                    >
                                      {notif.SenderName}
                                    </Typography>
                                  )}
                                  {notif.description}
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Chip
                                    label={notif.tag?.trim() || "General"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: "0.65rem",
                                      height: 20,
                                      fontWeight: 600,
                                      borderColor: `${iconConfig.color}40`,
                                      color: iconConfig.color,
                                      backgroundColor: `${iconConfig.color}10`,
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatTime(notif.sendDate)}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Unread Indicator */}
                              {isUnread && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#56C662",
                                    flexShrink: 0,
                                    mt: 0.5,
                                    boxShadow: (theme) =>
                                      `0 0 0 2px ${theme.palette.background.paper}`,
                                  }}
                                />
                              )}
                            </Paper>
                          );
                        })
                      )}
                    </Stack>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
}
